
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, GraduationCap, ExternalLink, BookOpen, Star, Sparkles, Filter, List, Info, Link as LinkIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../App.jsx';
import { collection, query, where, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import AuthLoadingScreen from '../AuthLoadingScreen.jsx';

const COURSES_PER_PAGE = 8;

// CATEGORIAS EXPANDIDAS PARA O FILTRO (Sincronizadas com Admin)
const ACADEMY_CATEGORIES = [
    "Todos", "Atendimento", "Finanças", "Técnico", "Marketing", "Vendas",
    "Gestão de Tempo", "Liderança", "Empreendedorismo",
    "Produtividade", "Relacionamento com Cliente", "Segurança no Trabalho", "Ética e Conduta"
];

const CourseCard = ({ title, category, imageUrl, url }) => (
    <div className="bg-white rounded-[2rem] p-4 shadow-sm border border-gray-100 flex items-center space-x-4 mb-3 active:scale-[0.98] transition-all">
        <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 bg-gray-50 flex items-center justify-center border border-gray-100">
            {imageUrl ? (
                <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
            ) : (
                <LinkIcon size={24} className="text-gray-300" />
            )}
        </div>
        <div className="flex-1 min-w-0 text-left">
            <div className="flex items-center space-x-2 mb-1.5">
                <span className="text-[8px] font-black uppercase tracking-widest text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100/50">
                    {category}
                </span>
            </div>
            <h3 className="font-bold text-gray-800 text-sm leading-tight truncate mb-2">{title}</h3>
            <button
                onClick={() => window.open(url, '_blank')}
                className="flex items-center bg-gray-900 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest active:scale-90 transition-all w-fit shadow-md shadow-gray-200"
            >
                Acessar <ExternalLink size={12} className="ml-2" />
            </button>
        </div>
    </div>
);

const ServiAcademyScreen = () => {
    const navigate = useNavigate();
    const { db } = useAppContext();
    const [courses, setCourses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [activeFilter, setActiveFilter] = useState('Todos');
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);

    const listContainerRef = useRef(null);

    const fetchCourses = useCallback(async (loadMore = false) => {
        if (!db) return;

        if (!loadMore) {
            setIsLoading(true);
            setCourses([]);
            setLastVisible(null);
            setHasMore(true);
        } else {
            if (!hasMore || isLoadingMore) return;
            setIsLoadingMore(true);
        }

        try {
            const baseQuery = collection(db, 'academy_courses');
            let constraints = [orderBy('createdAt', 'desc'), limit(COURSES_PER_PAGE)];

            if (activeFilter !== 'Todos') {
                constraints.unshift(where('category', '==', activeFilter));
            }

            if (loadMore && lastVisible) {
                constraints.push(startAfter(lastVisible));
            }

            const q = query(baseQuery, ...constraints);
            const snapshot = await getDocs(q);
            const newCourses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setCourses(prev => loadMore ? [...prev, ...newCourses] : newCourses);
            setHasMore(newCourses.length === COURSES_PER_PAGE);
        } catch (error) {
            console.warn("Erro ao carregar cursos:", error.message);
            setHasMore(false);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [db, activeFilter, lastVisible, hasMore, isLoadingMore]);

    useEffect(() => {
        fetchCourses(false);
    }, [activeFilter]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop < clientHeight * 1.5 && hasMore && !isLoadingMore) {
            fetchCourses(true);
        }
    };

    return (
        <div className="h-full bg-gray-50 flex flex-col">
            <header className="bg-white p-4 flex items-center justify-between shadow-sm sticky top-0 z-20">
                <div className="flex items-center">
                    <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100 mr-2 transition-colors">
                        <ArrowLeft size={20} className="text-gray-700" />
                    </button>
                    <div className="text-left">
                        <h1 className="font-black text-gray-800 text-lg uppercase tracking-tight leading-none">Academy</h1>
                        <p className="text-[10px] text-blue-600 font-bold uppercase tracking-widest mt-1">Evolução Profissional</p>
                    </div>
                </div>
                <GraduationCap className="text-gray-200" size={28} />
            </header>

            <div ref={listContainerRef} onScroll={handleScroll} className="flex-grow overflow-y-auto pb-24">
                {/* Banner de Boas-vindas */}
                <div className="p-4 pb-2">
                    <div className="bg-gradient-to-br from-slate-800 to-slate-950 rounded-[2.5rem] p-8 text-white text-left relative overflow-hidden shadow-xl">
                        <div className="relative z-10 flex flex-col space-y-4">
                            <div className="flex items-center space-x-2">
                                <Sparkles size={16} className="text-blue-400" />
                                <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Marketplace Academy</span>
                            </div>
                            <h2 className="text-2xl font-black leading-snug">Invista no seu Crescimento</h2>
                            <p className="text-slate-400 text-[11px] font-medium leading-relaxed max-w-[180px]">
                                Cursos externos selecionados por nossa equipe para impulsionar seu sucesso.
                            </p>
                        </div>
                        <GraduationCap className="absolute -right-4 -bottom-4 w-36 h-36 text-white opacity-5 -rotate-12 pointer-events-none" />
                    </div>
                </div>

                {/* MENSAGEM: EM DESENVOLVIMENTO */}
                <div className="px-4 mb-4">
                    <div className="bg-amber-50 border border-amber-100 p-4 rounded-[1.5rem] flex items-start space-x-3">
                        <div className="bg-amber-500 p-1.5 rounded-lg text-white shrink-0">
                            <Info size={14} />
                        </div>
                        <div className="text-left">
                            <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest leading-none">Atenção</p>
                            <p className="text-[11px] text-amber-800 font-bold mt-1 leading-tight">
                                Este recurso está em desenvolvimento e será atualizado em breve.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Filtro Dropdown */}
                <div className="relative mb-4 flex justify-between items-center px-5">
                    <button
                        onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
                        className="flex items-center space-x-2 px-4 py-2 bg-white border-2 border-gray-100 rounded-xl shadow-sm active:scale-95 transition-all"
                    >
                        <Filter className="w-4 h-4 text-blue-600" />
                        <span className="text-[10px] font-black uppercase text-gray-600">{activeFilter}</span>
                    </button>

                    {isFilterMenuOpen && (
                        <div className="absolute left-5 top-full mt-2 w-64 bg-white rounded-2xl shadow-2xl z-30 border p-2 animate-slide-up">
                            <div className="max-h-60 overflow-y-auto no-scrollbar">
                                {ACADEMY_CATEGORIES.map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => { setActiveFilter(cat); setIsFilterMenuOpen(false); }}
                                        className={`w-full text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest rounded-xl mb-1 ${activeFilter === cat ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Lista Dinâmica */}
                <div className="px-4">
                    {isLoading && courses.length === 0 ? (
                        <div className="text-center py-10 opacity-50"><p className="text-xs font-bold uppercase tracking-widest animate-pulse">Sincronizando conteúdos...</p></div>
                    ) : courses.length > 0 ? (
                        <>
                            {courses.map(course => (
                                <CourseCard key={course.id} {...course} />
                            ))}
                            {hasMore && (
                                <button
                                    onClick={() => fetchCourses(true)}
                                    className="w-full py-4 text-[10px] font-black text-blue-600 uppercase tracking-widest"
                                >
                                    {isLoadingMore ? "Carregando..." : "Ver mais conteúdos"}
                                </button>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-gray-100 mx-1">
                            <List size={40} className="text-gray-200 mx-auto mb-3" />
                            <p className="font-black text-gray-400 uppercase text-xs tracking-widest">Nenhum conteúdo disponível</p>
                        </div>
                    )}
                </div>

                {/* Rodapé Informativo */}
                <div className="mt-6 mx-4 p-8 bg-white rounded-[2.5rem] border border-gray-100 text-center shadow-sm">
                    <BookOpen className="mx-auto text-blue-100 mb-3" size={40} />
                    <p className="text-gray-800 font-black text-xs uppercase tracking-tight mb-1">Busca por mais conhecimento?</p>
                    <p className="text-gray-400 text-[10px] font-medium leading-relaxed px-4">
                        Estamos sempre buscando novas parcerias para sua carreira.
                    </p>
                </div>

                <p className="mt-8 text-[8px] text-gray-400 text-center px-10 pb-10 leading-tight uppercase font-black opacity-40 tracking-tighter">
                    O T-SERVI indica conteúdos de plataformas parceiras. Não nos responsabilizamos pelo conteúdo ou transações em sites externos.
                </p>
            </div>
        </div>
    );
};

export default ServiAcademyScreen;
